import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

const noop = () => {};

/**
 * Carries a completed ID.me verification from the component that hears about it
 * up to the page, which is the only place the outcome can be acted on.
 *
 * The verification is reported by the verify button, several levels down inside
 * the unverified program section - and that whole subtree is what the page stops
 * rendering once the customer counts as enrolled. Holding the outcome here means
 * it outlives the thing that reported it.
 *
 * Two pieces of state rather than one flag, deliberately. `verifiedProfile` is
 * the durable fact that this customer verified, and the page keeps reading
 * enrollment off it for as long as it is mounted. `isToastOpen` is only the
 * confirmation sitting over that page, and it is gone within seconds. Collapsing
 * them would mean dismissing the toast also un-enrolls the customer, since the
 * enrollment API cannot yet confirm what the toast just confirmed - see the
 * derivation in pages/LandingPage.jsx.
 */
export const MilitaryVerificationSuccessContext = createContext({
  verifiedProfile: null,
  isToastOpen: false,
  markVerified: noop,
  dismissToast: noop,
});

export const MilitaryVerificationSuccessProvider = ({ children }) => {
  const [verifiedProfile, setVerifiedProfile] = useState(null);
  const [isToastOpen, setIsToastOpen] = useState(false);

  /* The profile is always present on the way in: the platform reports a missing
  /* one as an error instead, and a denial is turned away by the caller before it
  /* reaches here, so anything that arrives is a customer who verified. */
  const markVerified = useCallback((profile) => {
    setVerifiedProfile(profile);
    setIsToastOpen(true);
  }, []);

  const dismissToast = useCallback(() => setIsToastOpen(false), []);

  const value = useMemo(
    () => ({ verifiedProfile, isToastOpen, markVerified, dismissToast }),
    [verifiedProfile, isToastOpen, markVerified, dismissToast],
  );

  return (
    <MilitaryVerificationSuccessContext.Provider value={value}>
      {children}
    </MilitaryVerificationSuccessContext.Provider>
  );
};

MilitaryVerificationSuccessProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useMilitaryVerificationSuccess = () =>
  useContext(MilitaryVerificationSuccessContext);
